import { inject, injectable } from 'inversify'
import { getCookieByName } from '@/utils/cookies'
import type LoggerService from '@/services/loggerService'
import { SERVICE_TYPES } from '@/core/container/types'

export type CookieYesConsentData = {
  consentid: string | null
  consent: boolean
  action: boolean
  necessary: boolean
  functional: boolean
  analytics: boolean
  performance: boolean
  advertisement: boolean
  other: boolean
  lastRenewedDate: number | null
}

@injectable()
export default class CookieConsentService {
  private readonly CONSENT_COOKIE_NAME = 'cookieyes-consent'

  constructor(@inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService) {}

  /**
   * Parses the value of the CookieYes consent cookie
   * Format: key1:value1,key2:value2,...
   * Example: "consentid:cDZsTnk3ZjJWQlowaFRoT0tMOHV2RWlFU1RaQXRHb0w,consent:yes,action:yes,necessary:yes,functional:yes,analytics:yes,performance:yes,advertisement:yes,other:yes,lastRenewedDate:1742821172000"
   */
  private parseCookieYesConsent(cookieValue: string): CookieYesConsentData | null {
    try {
      const pairs = cookieValue.split(',')
      const data: Record<string, string> = {}

      pairs.forEach((pair) => {
        const [key, value] = pair.split(':')
        if (key && value) {
          data[key.trim()] = value.trim()
        }
      })

      return {
        consentid: data.consentid || null,
        consent: data.consent === 'yes',
        action: data.action === 'yes',
        necessary: data.necessary === 'yes',
        functional: data.functional === 'yes',
        analytics: data.analytics === 'yes',
        performance: data.performance === 'yes',
        advertisement: data.advertisement === 'yes',
        other: data.other === 'yes',
        lastRenewedDate: data.lastRenewedDate ? parseInt(data.lastRenewedDate, 10) : null,
      }
    } catch (error) {
      this.loggerService.error('tracking', `Failed to parse consent cookie: ${error}`, 'CookieConsent')
      return null
    }
  }

  public getConsentData(): CookieYesConsentData | null {
    const cookieValue = getCookieByName(this.CONSENT_COOKIE_NAME)

    if (!cookieValue) {
      return null
    }

    return this.parseCookieYesConsent(cookieValue)
  }

  public hasMarketingConsent(): boolean {
    const consentData = this.getConsentData()

    if (!consentData) {
      return false
    }

    if (!consentData.consent) {
      return false
    }

    return consentData.analytics && consentData.advertisement
  }

  /**
   * Checks consent to advertising trackers that do not depend on analytics
   * (OpenAI pixel and etc.)
   */
  public hasAdvertisementConsent(): boolean {
    const consentData = this.getConsentData()

    if (!consentData) {
      return false
    }

    return consentData.consent && consentData.advertisement
  }

  /**
   * Checks consent to analytics trackers
   * (PostHog, HotJar, Convert, Zuko and etc.)
   */
  public hasAnalyticsConsent(): boolean {
    const consentData = this.getConsentData()

    if (!consentData) {
      return false
    }

    return consentData.consent && consentData.analytics
  }

  /**
   * Checks consent to functional trackers
   * (e.g. Zendesk chat)
   */
  public hasFunctionalConsent(): boolean {
    const consentData = this.getConsentData()

    if (!consentData) {
      return false
    }

    return consentData.consent && consentData.functional
  }

  /**
   * For debugging
   */
  public getRawConsentCookie(): string | null {
    return getCookieByName(this.CONSENT_COOKIE_NAME)
  }

  public hasConsentCookie(): boolean {
    return getCookieByName(this.CONSENT_COOKIE_NAME) !== null
  }

  public logConsentState(): void {
    const rawCookie = this.getRawConsentCookie()
    const consentData = this.getConsentData()

    this.loggerService.group(
      'tracking',
      'Cookie Consent State',
      () => {
        this.loggerService.log('tracking', `Cookie exists: ${this.hasConsentCookie()}`, 'CookieConsent')
        this.loggerService.log('tracking', `Raw cookie: ${rawCookie}`, 'CookieConsent')
        this.loggerService.log('tracking', `Parsed data:`, 'CookieConsent')
        this.loggerService.raw('tracking', consentData)
        this.loggerService.log('tracking', `Has marketing consent: ${this.hasMarketingConsent()}`, 'CookieConsent')
        this.loggerService.log(
          'tracking',
          `Has advertisement consent: ${this.hasAdvertisementConsent()}`,
          'CookieConsent',
        )
        this.loggerService.log('tracking', `Has analytics consent: ${this.hasAnalyticsConsent()}`, 'CookieConsent')
        this.loggerService.log('tracking', `Has functional consent: ${this.hasFunctionalConsent()}`, 'CookieConsent')
      },
      'CookieConsent',
    )
  }
}
